import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, ShieldCheck, Star, Copy, CheckCircle2, AlertCircle, ChevronDown } from "lucide-react";
import { useGameAuth } from "@/hooks/useGameAuth";
import { useSolPrice } from "@/hooks/useSolPrice";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { GAME_STARS_CHECKOUT_SESSION_KEY } from "@/pages/GameLanding";
import { getSupabaseFunctionErrorMessage } from "@/lib/supabaseFunctionError";

const ADMIN_WALLET = "ADJkonbgxndjbyL5XHfBLkVhEGiTUBChK9YMg8PtmPKa";

const PACKAGES = [
  { usd: 5, stars: 10, label: "10 Stars", doubleBalance: false },
  { usd: 10, stars: 15, label: "15 Stars", doubleBalance: false },
  { usd: 15, stars: 50, label: "50 Stars", doubleBalance: false },
  { usd: 20, stars: 0, label: "Double Balance", doubleBalance: true },
] as const;

// Auto-detection polling config
const POLL_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

export default function GameStarsCheckout() {
  const { session, player, loading } = useGameAuth();
  const { price: solPrice, isLoading: priceLoading, error: priceError, refresh: refreshPrice } = useSolPrice();
  const location = useLocation();
  const navigate = useNavigate();
  const [restoringSession, setRestoringSession] = useState(true);
  const [txSignature, setTxSignature] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [copiedWallet, setCopiedWallet] = useState(false);
  const [copiedAmount, setCopiedAmount] = useState(false);
  const [lockedSolPrice, setLockedSolPrice] = useState<number>(0);

  // Auto-detection state
  const [detecting, setDetecting] = useState(false);
  const [detectStartedAt, setDetectStartedAt] = useState<number | null>(null);
  const [showManualFallback, setShowManualFallback] = useState(false);
  const pollTimerRef = useRef<number | null>(null);
  const pollDeadlineRef = useRef<number>(0);

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const playerId = query.get("playerId") || "";
  const packageIndex = Number(query.get("pkg") || 1);
  const pkg = PACKAGES[Math.max(0, Math.min(PACKAGES.length - 1, Number.isFinite(packageIndex) ? packageIndex : 1))];
  const effectiveSolPrice = lockedSolPrice > 0 ? lockedSolPrice : solPrice;
  const solNeeded = pkg.usd > 0 && effectiveSolPrice > 0 ? pkg.usd / effectiveSolPrice : 0;
  const solDisplay = solNeeded > 0 ? solNeeded.toFixed(6) : "—";
  const returnUrl = query.get("returnTo") || "/game/dashboard";

  useEffect(() => {
    if (solPrice > 0 && lockedSolPrice === 0) {
      setLockedSolPrice(solPrice);
    }
  }, [solPrice, lockedSolPrice]);

  const handleRefreshPrice = () => {
    setLockedSolPrice(0);
    refreshPrice();
  };

  // Restore session if returning from external context
  useEffect(() => {
    if (loading) return;
    if (session) {
      setRestoringSession(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const raw = sessionStorage.getItem(GAME_STARS_CHECKOUT_SESSION_KEY);
        if (!raw) return;
        const saved = JSON.parse(raw) as {
          access_token?: string;
          refresh_token?: string;
          playerId?: string;
          timestamp?: number;
        };
        const isFresh = !!saved.timestamp && Date.now() - saved.timestamp < 30 * 60 * 1000;
        if (!isFresh || saved.playerId !== playerId || !saved.access_token || !saved.refresh_token) {
          sessionStorage.removeItem(GAME_STARS_CHECKOUT_SESSION_KEY);
          return;
        }
        await supabase.auth.setSession({
          access_token: saved.access_token,
          refresh_token: saved.refresh_token,
        });
      } catch {
        sessionStorage.removeItem(GAME_STARS_CHECKOUT_SESSION_KEY);
      } finally {
        if (!cancelled) setRestoringSession(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loading, playerId, session]);

  // Cleanup poller on unmount
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        window.clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, []);

  const copy = async (text: string, which: "wallet" | "amount") => {
    try {
      await navigator.clipboard.writeText(text);
      if (which === "wallet") {
        setCopiedWallet(true);
        setTimeout(() => setCopiedWallet(false), 1500);
      } else {
        setCopiedAmount(true);
        setTimeout(() => setCopiedAmount(false), 1500);
      }
      toast.success("Copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const stopPolling = () => {
    if (pollTimerRef.current) {
      window.clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    setDetecting(false);
  };

  const pollOnce = async (sinceTs: number) => {
    if (!player) return;
    try {
      const { data, error } = await supabase.functions.invoke("auto-detect-star-payment", {
        body: {
          player_id: player.id,
          package_usd: pkg.usd,
          quoted_sol_price: effectiveSolPrice,
          since_ts: sinceTs,
        },
      });
      if (error) {
        // Soft error: keep polling, don't break the flow
        console.warn("auto-detect error:", error);
      } else {
        const res = data as any;
        if (res?.status === "credited") {
          stopPolling();
          toast.success(
            res.already_credited
              ? "Purchase already credited ✅"
              : `+${res.stars_credited} Stars credited ✅ New balance: $${Number(res.new_balance).toFixed(2)}`,
          );
          navigate(returnUrl, { replace: true });
          return;
        }
        if (res?.status === "error") {
          console.warn("auto-detect server error:", res.error);
        }
      }
    } catch (e) {
      console.warn("auto-detect exception:", e);
    }

    // Schedule next poll if still within deadline
    if (Date.now() < pollDeadlineRef.current) {
      pollTimerRef.current = window.setTimeout(() => pollOnce(sinceTs), POLL_INTERVAL_MS);
    } else {
      stopPolling();
      setShowManualFallback(true);
      toast.error("We couldn't detect your payment automatically. Please paste your transaction signature below.");
    }
  };

  const handleIvePaid = () => {
    if (!player || detecting) return;
    const startedAt = Math.floor(Date.now() / 1000);
    setDetectStartedAt(startedAt);
    setDetecting(true);
    pollDeadlineRef.current = Date.now() + POLL_TIMEOUT_MS;
    // First poll after a tiny delay to give the network time to propagate
    pollTimerRef.current = window.setTimeout(() => pollOnce(startedAt), 2000);
  };

  const handleVerifyManual = async () => {
    if (!player) return;
    const sig = txSignature.trim();
    if (!sig || sig.length < 40) {
      toast.error("Paste a valid transaction signature");
      return;
    }
    setVerifying(true);
    try {
      const { data, error } = await supabase.functions.invoke("recover-star-purchase", {
        body: {
          tx_signature: sig,
          player_id: player.id,
          package_usd: pkg.usd,
          quoted_sol_price: effectiveSolPrice,
        },
      });
      if (error) {
        throw new Error(await getSupabaseFunctionErrorMessage(error, "Verification failed"));
      }
      const res = data as any;
      if (res?.success) {
        toast.success(
          res.already_credited
            ? "Purchase already credited ✅"
            : `+${res.stars_credited} Stars credited ✅ New balance: $${Number(res.new_balance).toFixed(2)}`,
        );
        navigate(returnUrl, { replace: true });
      } else {
        toast.error(res?.error || "Verification failed");
      }
    } catch (e: any) {
      toast.error(e?.message || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  if (loading || restoringSession) return null;
  if (!session || !player) return <Navigate to="/game" replace />;
  if (player.id !== playerId) {
    return <Navigate to="/game/dashboard" replace />;
  }

  return (
    <div className="min-h-dvh game-bg flex items-center justify-center px-4 py-8 text-white">
      <Card className="w-full max-w-md border border-border bg-card/95 backdrop-blur-xl">
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <ShieldCheck className="h-7 w-7" />
          </div>
          <CardTitle className="text-xl text-foreground">Manual SOL payment</CardTitle>
          <CardDescription className="text-muted-foreground">
            Send SOL from any wallet to the treasury, then click "I've paid" — we'll detect it automatically.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="rounded-lg border border-border bg-background/60 p-4 space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Package</span>
              <span className="font-semibold text-foreground">{pkg.label}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Amount (USD)</span>
              <span className="font-semibold text-foreground">${pkg.usd}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">SOL price</span>
              <span className="font-semibold text-foreground flex items-center gap-1">
                {priceLoading && lockedSolPrice === 0 ? <Loader2 className="h-3 w-3 animate-spin" />
                  : priceError && lockedSolPrice === 0 ? <span className="text-red-400">Error</span>
                  : `$${effectiveSolPrice.toFixed(2)}`}
                <button
                  onClick={handleRefreshPrice}
                  disabled={detecting}
                  className="text-muted-foreground hover:text-foreground text-[10px] underline disabled:opacity-50"
                >
                  refresh
                </button>
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">SOL to send</span>
              <span className="font-semibold text-primary">{solDisplay} SOL</span>
            </div>
          </div>

          <div className="rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-3 text-xs text-yellow-100 flex gap-2">
            <AlertCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
            <span>
              Open your Solana wallet (Phantom, Solflare, Backpack, exchange, etc.) and send the EXACT
              amount of SOL to the treasury address below. Then tap "I've paid".
            </span>
          </div>

          <div className="space-y-2">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Treasury wallet</label>
            <div className="flex items-center gap-2 bg-background/60 rounded-lg p-2 border border-border">
              <code className="flex-1 text-[11px] text-primary break-all">{ADMIN_WALLET}</code>
              <button onClick={() => copy(ADMIN_WALLET, "wallet")} className="text-muted-foreground hover:text-foreground p-1">
                {copiedWallet ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Amount to send</label>
            <div className="flex items-center gap-2 bg-background/60 rounded-lg p-2 border border-border">
              <code className="flex-1 text-sm text-yellow-300 font-bold">{solDisplay} SOL</code>
              <span className="text-[11px] text-muted-foreground">(${pkg.usd})</span>
              <button onClick={() => copy(solDisplay, "amount")} className="text-muted-foreground hover:text-foreground p-1">
                {copiedAmount ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Tolerance ±0.005 SOL or ±$1. Sending the wrong amount may not match a package.
            </p>
          </div>

          {/* Reassuring message while detecting */}
          {detecting && (
            <div className="rounded-lg border border-primary/40 bg-primary/10 p-3 text-xs text-foreground flex gap-2">
              <Loader2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0 animate-spin" />
              <div className="space-y-1">
                <p className="font-semibold">Payment detection in progress…</p>
                <p className="text-muted-foreground">
                  This may take a few seconds. Your Stars will appear in your balance shortly — please don't close this page.
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Button variant="outline" asChild disabled={verifying || detecting}>
              <Link to={returnUrl}>Back to game</Link>
            </Button>
            <Button onClick={handleIvePaid} disabled={detecting || verifying}>
              {detecting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Detecting…</>
              ) : (
                <><Star className="h-4 w-4 mr-2" /> I've paid</>
              )}
            </Button>
          </div>

          {/* Emergency manual fallback — only for failed payments */}
          <div className="mt-6 pt-3 border-t border-dashed border-border/50 opacity-70">
            <button
              type="button"
              onClick={() => setShowManualFallback((v) => !v)}
              className="w-full flex items-center justify-between text-[10px] text-muted-foreground/70 hover:text-foreground"
            >
              <span>⚠ Payment failed or not detected? Recover with TX signature</span>
              <ChevronDown className={`w-3 h-3 transition-transform ${showManualFallback ? "rotate-180" : ""}`} />
            </button>
            {showManualFallback && (
              <div className="space-y-2 mt-3">
                <p className="text-[10px] text-muted-foreground">
                  Use this ONLY if your payment was sent but auto-detection failed. You don't need this for a normal purchase.
                </p>
                <Input
                  value={txSignature}
                  onChange={(e) => setTxSignature(e.target.value)}
                  placeholder="Paste tx signature from your wallet"
                  className="bg-background/60 border-border text-foreground text-xs"
                  disabled={verifying}
                />
                <Button
                  onClick={handleVerifyManual}
                  disabled={verifying || !txSignature.trim()}
                  variant="secondary"
                  size="sm"
                  className="w-full"
                >
                  {verifying ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Verifying...</>
                  ) : (
                    <>Recover payment manually</>
                  )}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
